import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { generalAnimations } from 'app/shared/animations/animations';
import { AuthService } from 'app/shared/authentication/services/auth.service';
import { LanguageCulureNameEnum } from 'app/shared/layout/models/languageCulureNameEnum';
import { PortalType } from 'app/shared/portal/models/portal-type.enum';
import { Identifier, LocalStorageService } from 'app/shared/storage/services/local-storage.service';
import { Subscription } from 'rxjs';

import { NavigationHandlerService } from '../../services/navigation-handler.service';
import { LoginType, createLoginType } from '../../models/login-type.model';
import { ClinicOnlineBookingSetup } from 'app/shared/portal/models/clinic-online-booking-setup.model';

@Component({
  selector: 'core-auth-login',
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: generalAnimations,
  standalone: false,
})
export class LoginComponent implements OnInit, OnDestroy {
  @Input() canReturn = false;
  @Input() previousCanReturn = false;
  @Input() loginType!: LoginType;
  @Output() forgotPasswordEmitter: EventEmitter<any> = new EventEmitter<any>();
  @Output() loginSelectionEmitter: EventEmitter<any> = new EventEmitter<any>();
  @Output() emailConfirmationEmitter: EventEmitter<any> = new EventEmitter<any>();
  @Output() oneTimeLoginEmailEmitter: EventEmitter<any> = new EventEmitter<any>();
  @Output() oneTimeLoginSmsEmitter: EventEmitter<any> = new EventEmitter<any>();
  @Output() closeModalEmitter: EventEmitter<any> = new EventEmitter<any>();
  loginForm?: UntypedFormGroup;
  isSubmitted = false;
  passwordMode = 'password';
  portalSetup?: ClinicOnlineBookingSetup;

  private subscription: Subscription = new Subscription();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private ref: ChangeDetectorRef,
    private authService: AuthService,
    private localStorageService: LocalStorageService,
    private navigationHandlerService: NavigationHandlerService
  ) {}

  ngOnInit(): void {
    if (!this.loginType) {
      this.loginType = createLoginType();
    }
    const portalSetupJson = localStorage.getItem('portalSetup');
    if (portalSetupJson) {
      const portalSetup = JSON.parse(portalSetupJson);
      this.portalSetup = portalSetup;
    }
    this.fillFormGroup();
  }

  fillFormGroup(): void {
    this.loginForm = this.formBuilder.group({
      login: [this.loginType.value, [Validators.required]],
      password: ['', Validators.required],
    });
    this.ref.markForCheck();
  }

  onLoginClick(): void {
    this.loginForm?.markAllAsTouched();
    if (this.loginForm?.valid) {
      const model = {
        userName: this.loginForm?.value.login,
        password: this.loginForm?.value.password,
        portalType: PortalType.Referral,
      };
      this.subscription.add(
        this.authService.loginUser(model).subscribe(
          res => {
            const cmUser = this.authService.processToken(res.token);
            if (cmUser.correspondenceLanguageId) {
              this.localStorageService.setStorageItem(
                Identifier.TYPE_LANGUAGE,
                LanguageCulureNameEnum[cmUser.correspondenceLanguageId]
              );
            }

            this.navigationHandlerService.handleNavigation();
            this.closeModalEmitter.emit();
          },
          () => {
            this.ref.markForCheck();
          }
        )
      );
    }
  }

  onPasswordViewClick(): void {
    this.passwordMode = this.passwordMode === 'text' ? 'password' : 'text';
    this.ref.markForCheck();
  }

  displayRequiredError(control: AbstractControl): boolean {
    return control.hasError('required') && (control.touched || this.isSubmitted);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onSwitchToForgotPassword(): void {
    this.forgotPasswordEmitter.emit();
  }

  onSwitchToLoginSelection(): void {
    this.loginSelectionEmitter.emit();
  }

  onSwitchToEmailConfirmation(): void {
    this.emailConfirmationEmitter.emit();
  }

  onSelectEmail(): void {
    this.oneTimeLoginEmailEmitter.emit();
  }

  onSelectSms(): void {
    this.oneTimeLoginSmsEmitter.emit();
  }
}
